import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchLeaveBalanceApi,
  fetchManagersApi,
  fetchAllLeaveApplicationsApi,
  applyLeaveApi,
  grantCompOffApi,
  withdrawLeaveApi,
  fetchManagerPendingApplicationsApi,
  approveOrRejectLeaveStatusApi,
  cancelLeaveApi,
  approveOrRejectCancelLeaveApplicationApi,
  fetchUserLeaveBalanceApi,
} from "../../api/leave.api";
import { approveOrRejectRegularizationApi } from "../../api/regularization.api";

interface LeaveState {
  loading: boolean;
  balances: Record<string, number>;
  managers: { id: string; name: string }[];
  pendingApplications: any[];
  leaveHistory: any[];
  managerPendingApplications: any[];
}

const initialState: LeaveState = {
  loading: false,
  balances: {},
  managers: [],
  pendingApplications: [],
  leaveHistory: [],
  managerPendingApplications: [],
};

interface ApproveRejectPayload {
  applicationId: string;
  action: "approved" | "rejected";
}

// 👉 Thunk for normal leave
export const applyLeave = createAsyncThunk(
  "leave/applyLeave",
  async (formData: any, { rejectWithValue }) => {
    try {
      return await applyLeaveApi(formData);
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to apply leave");
    }
  }
);

export const fetchLeaveBalance = createAsyncThunk(
  "leave/fetchBalance",
  async (_, { rejectWithValue }) => {
    try {
      return await fetchLeaveBalanceApi();
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to load balances");
    }
  }
);

export const fetchManagers = createAsyncThunk(
  "leave/fetchManagers",
  async (_, { rejectWithValue }) => {
    try {
      return await fetchManagersApi();
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to load managers");
    }
  }
);

// 👉 Thunk for comp-off grant
export const grantCompOff = createAsyncThunk(
  "leave/grantCompOff",
  async (formData: any, { rejectWithValue }) => {
    try {
      return await grantCompOffApi(formData);
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to grant comp-off");
    }
  }
);

export const fetchAllLeaveApplications = createAsyncThunk(
  "leave/fetchAllLeaveApplications",
  async (_, { rejectWithValue }) => {
    try {
      return await fetchAllLeaveApplicationsApi();
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to fetch applications");
    }
  }
);

export const withdrawLeave = createAsyncThunk(
  "leave/withdrawLeave",
  async (applicationId: string, { rejectWithValue }) => {
    try {
      return await withdrawLeaveApi(applicationId);
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to withdraw leave");
    }
  }
);

// Thunk for manager pending
export const fetchManagerPendingApplications = createAsyncThunk(
  "leave/fetchManagerPendingApplications",
  async (_, { rejectWithValue }) => {
    try {
      return await fetchManagerPendingApplicationsApi();
    } catch (err: any) {
      return rejectWithValue(
        err.message || "Failed to fetch manager pending applications"
      );
    }
  }
);

// in leaveSlice.ts
export const approveOrRejectLeave = createAsyncThunk(
  "leave/approveOrRejectLeave",
  async (
    {
      applicationId,
      action,
    }: { applicationId: string; action: "approved" | "rejected" },
    { rejectWithValue }
  ) => {
    try {
      return await approveOrRejectLeaveStatusApi({ applicationId, action });
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to update status");
    }
  }
);

export const approveOrRejectRegularization = createAsyncThunk<
  any,
  ApproveRejectPayload,
  { rejectValue: string }
>(
  "leave/approveOrRejectRegularization",
  async (payload, { rejectWithValue }) => {
    try {
      const data = await approveOrRejectRegularizationApi(payload);
      return data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const cancelLeave = createAsyncThunk(
  "leave/cancelLeave",
  async (applicationId: string, { rejectWithValue }) => {
    try {
      return await cancelLeaveApi(applicationId);
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to cancel leave");
    }
  }
);

export const approveOrRejectCancelLeaveApplication = createAsyncThunk(
  "cancelLeave/approveOrRejectCancelLeaveApplication",
  async (
    {
      applicationId,
      action,
    }: { applicationId: string; action: "approved" | "rejected" },
    { rejectWithValue }
  ) => {
    try {
      const response = await approveOrRejectCancelLeaveApplicationApi({
        applicationId,
        action,
      });
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Failed to update cancel leave application"
      );
    }
  }
);

export const fetchUserLeaveBalance = createAsyncThunk(
  "leave/fetchUserBalance",
  async (userId: string, { rejectWithValue }) => {
    try {
      return await fetchUserLeaveBalanceApi(userId);
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to fetch user balance");
    }
  }
);

const leaveSlice = createSlice({
  name: "leave",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Apply Leave
      .addCase(applyLeave.pending, (state) => {
        state.loading = true;
      })
      .addCase(applyLeave.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(applyLeave.rejected, (state) => {
        state.loading = false;
      })

      // Grant Comp-Off
      .addCase(grantCompOff.pending, (state) => {
        state.loading = true;
      })
      .addCase(grantCompOff.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(grantCompOff.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchLeaveBalance.fulfilled, (state, action) => {
        state.balances = action.payload;
      })

      .addCase(fetchManagers.fulfilled, (state, action) => {
        state.managers = action.payload;
      })
      .addCase(fetchAllLeaveApplications.fulfilled, (state, action) => {
        const data = action.payload || {};
        state.pendingApplications = data.pendingApplications || [];
        state.leaveHistory = data.leaveHistory || [];
      })
      .addCase(withdrawLeave.pending, (state) => {
        state.loading = true;
      })
      .addCase(withdrawLeave.fulfilled, (state, action) => {
        state.pendingApplications = state.pendingApplications.filter(
          (application) => application._id !== action.payload._id
        );
        state.leaveHistory.unshift(action.payload);
        state.loading = false;
      })

      .addCase(withdrawLeave.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchManagerPendingApplications.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchManagerPendingApplications.fulfilled, (state, action) => {
        state.managerPendingApplications = action.payload || [];
        state.loading = false;
      })
      .addCase(fetchManagerPendingApplications.rejected, (state) => {
        state.loading = false;
      })
      .addCase(approveOrRejectLeave.pending, (state) => {
        state.loading = true;
      })
      .addCase(approveOrRejectLeave.fulfilled, (state, action) => {
        state.managerPendingApplications =
          state.managerPendingApplications.filter(
            (app) => app._id !== action.payload._id
          );
        state.loading = false;
      })
      .addCase(approveOrRejectLeave.rejected, (state) => {
        state.loading = false;
      })
      .addCase(approveOrRejectRegularization.pending, (state) => {
        state.loading = true;
      })
      .addCase(approveOrRejectRegularization.fulfilled, (state, action) => {
        state.managerPendingApplications =
          state.managerPendingApplications.filter(
            (app) => app._id !== action.payload._id
          );
        state.loading = false;
      })
      .addCase(approveOrRejectRegularization.rejected, (state) => {
        state.loading = false;
      })
      .addCase(cancelLeave.pending, (state) => {
        state.loading = true;
      })
      .addCase(cancelLeave.fulfilled, (state, action) => {
        const leave = action.payload;
        state.leaveHistory = state.leaveHistory.filter(
          (app) => app._id !== leave._id
        );
        state.pendingApplications.unshift(leave);
        state.loading = false;
      })
      .addCase(cancelLeave.rejected, (state) => {
        state.loading = false;
      })

      .addCase(approveOrRejectCancelLeaveApplication.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        approveOrRejectCancelLeaveApplication.fulfilled,
        (state, action) => {
          const leave = action.payload;

          state.managerPendingApplications =
            state.managerPendingApplications.filter(
              (app) => app._id !== leave._id
            );
          state.leaveHistory.unshift(leave);

          state.loading = false;
        }
      )
      .addCase(approveOrRejectCancelLeaveApplication.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchUserLeaveBalance.fulfilled, (state, action) => {
        state.balances = action.payload;
      });
  },
});

export default leaveSlice.reducer;
